import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DescriptionIcon from "@mui/icons-material/Description";
import SchoolIcon from "@mui/icons-material/School";
import BadgeIcon from "@mui/icons-material/Badge";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import technicianExpedienteService from "../../../api/technicianExpedienteService";
import {
  DOCUMENT_TYPES,
  CAPACITACION_TYPES,
  PERSONAL_FIELDS,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
  formatExpedienteDate,
  toDateInputValue,
} from "../expedienteConstants";
import { C, SCROLL } from "../theme";

const ALLOWED_EXTS = ".pdf,.png,.jpeg,.jpg";

function validateFile(file) {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return "Solo PDF, PNG o JPEG";
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "El archivo excede 10 MB";
  }
  return null;
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.85,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderBottom: `1px solid ${C.border}`,
          bgcolor: "#fafafa",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16, color: C.primary }} />
        <Typography sx={{ fontWeight: 700, fontSize: 12, color: C.text, flex: 1 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", ...SCROLL, p: 1.25 }}>
        {children}
      </Box>
    </Box>
  );
}

function DocumentRow({
  label,
  document,
  canEdit,
  uploading,
  onUpload,
  onView,
}) {
  const inputRef = useRef(null);
  const hasFile = Boolean(document?.key);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.6,
        gap: 0.5,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Typography sx={{ fontSize: 12, color: C.textSoft, flex: 1, minWidth: 0 }} noWrap>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        {hasFile ? (
          <>
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
              label="Cargado"
              sx={{
                height: 18,
                fontSize: 9,
                bgcolor: "#dcfce7",
                color: "#16a34a",
                fontWeight: 600,
                "& .MuiChip-icon": { color: "#16a34a" },
              }}
            />
            <Tooltip title="Ver documento">
              <IconButton size="small" onClick={() => onView(document.key)}>
                <VisibilityIcon sx={{ fontSize: 16, color: C.primary }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Chip
            size="small"
            label="Pendiente"
            sx={{
              height: 18,
              fontSize: 9,
              bgcolor: "#fef3c7",
              color: C.warning,
              fontWeight: 600,
            }}
          />
        )}
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED_EXTS}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onUpload(file);
              }}
            />
            <Tooltip title={hasFile ? "Reemplazar" : "Subir"}>
              <span>
                <IconButton
                  size="small"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                >
                  {uploading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <UploadFileIcon sx={{ fontSize: 16, color: C.primary }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function TechnicianExpedientePanel({
  technician,
  canEdit = false,
  onExpedienteUpdate,
}) {
  const [expediente, setExpediente] = useState(null);
  const [personalDraft, setPersonalDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [error, setError] = useState(null);

  const technicianId = technician?._id;

  const loadExpediente = useCallback(async () => {
    if (!technicianId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await technicianExpedienteService.getExpediente(technicianId);
      setExpediente(data);
      setPersonalDraft(data.personal || {});
      onExpedienteUpdate?.(data);
    } catch (err) {
      console.error("Error loading expediente:", err);
      setError("No se pudo cargar el expediente");
    } finally {
      setLoading(false);
    }
  }, [technicianId, onExpedienteUpdate]);

  useEffect(() => {
    loadExpediente();
  }, [loadExpediente]);

  const handleSavePersonal = async () => {
    if (!technicianId) return;
    try {
      setSavingPersonal(true);
      setError(null);
      const data = await technicianExpedienteService.updatePersonal(
        technicianId,
        personalDraft
      );
      setExpediente((prev) => ({ ...prev, ...data }));
      onExpedienteUpdate?.({ ...expediente, ...data });
    } catch (err) {
      setError(err.response?.data?.message || "Error al guardar datos personales");
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleUploadDocument = async (docType, file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploadingKey(docType);
      setError(null);
      const data = await technicianExpedienteService.uploadDocument(
        technicianId,
        docType,
        file
      );
      setExpediente((prev) => ({ ...prev, ...data }));
      onExpedienteUpdate?.({ ...expediente, ...data });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Error al subir documento");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleUploadCapacitacion = async (capId, file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploadingKey(`cap-${capId}`);
      setError(null);
      const data = await technicianExpedienteService.uploadCapacitacion(
        technicianId,
        capId,
        file
      );
      setExpediente((prev) => ({ ...prev, ...data }));
      onExpedienteUpdate?.({ ...expediente, ...data });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Error al subir capacitación");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleViewDocument = async (key) => {
    try {
      const url = await technicianExpedienteService.getDocumentViewUrl(key);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("No se pudo abrir el documento");
    }
  };

  if (!technician) return null;

  const documentsByType = (expediente?.documents || []).reduce((acc, doc) => {
    acc[doc.type] = doc;
    return acc;
  }, {});

  const capacitacionesById = (expediente?.capacitaciones || []).reduce((acc, cap) => {
    acc[cap.id] = cap;
    return acc;
  }, {});

  const completeness = expediente?.completeness ?? 0;

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2,
          border: `1px solid ${C.border}`,
          bgcolor: C.primarySoft,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <FolderOpenIcon sx={{ fontSize: 20, color: C.primary }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.text }}>
            Expediente — {technician.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={completeness}
              sx={{
                flex: 1,
                maxWidth: 160,
                height: 5,
                borderRadius: 3,
                bgcolor: "#dbeafe",
                "& .MuiLinearProgress-bar": { bgcolor: C.primary, borderRadius: 3 },
              }}
            />
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.primary }}>
              {completeness}%
            </Typography>
          </Box>
        </Box>
        <Chip
          label={canEdit ? "Editable" : "Solo lectura"}
          size="small"
          sx={{
            height: 22,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: canEdit ? "#dcfce7" : "#fff",
            color: canEdit ? "#16a34a" : C.textSoft,
          }}
        />
      </Box>

      {error && (
        <Typography sx={{ fontSize: 11, color: C.danger, px: 0.5 }}>{error}</Typography>
      )}

      {loading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: C.primary }} />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          <SectionCard title="Datos personales" icon={BadgeIcon}>
            {PERSONAL_FIELDS.map(({ key, label, type }) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.6,
                  gap: 1,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: C.textSoft, flexShrink: 0 }}>
                  {label}
                </Typography>
                {canEdit ? (
                  <TextField
                    size="small"
                    type={type === "date" ? "date" : "text"}
                    value={
                      type === "date"
                        ? toDateInputValue(personalDraft[key])
                        : personalDraft[key] ?? ""
                    }
                    onChange={(e) =>
                      setPersonalDraft((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-input": { fontSize: 12, py: 0.5, textAlign: "right" },
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                    {type === "date"
                      ? formatExpedienteDate(personalDraft[key])
                      : personalDraft[key] || "—"}
                  </Typography>
                )}
              </Box>
            ))}
            {canEdit && (
              <Button
                size="small"
                variant="contained"
                startIcon={
                  savingPersonal ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SaveIcon sx={{ fontSize: 14 }} />
                  )
                }
                disabled={savingPersonal}
                onClick={handleSavePersonal}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontSize: 11,
                  bgcolor: C.primary,
                  "&:hover": { bgcolor: C.primary },
                }}
              >
                Guardar datos
              </Button>
            )}
          </SectionCard>

          <SectionCard title="Documentos legales" icon={DescriptionIcon}>
            {DOCUMENT_TYPES.map(({ id, label }) => (
              <DocumentRow
                key={id}
                label={label}
                document={documentsByType[id]}
                canEdit={canEdit}
                uploading={uploadingKey === id}
                onUpload={(file) => handleUploadDocument(id, file)}
                onView={handleViewDocument}
              />
            ))}
          </SectionCard>

          <SectionCard title="Capacitaciones" icon={SchoolIcon}>
            {CAPACITACION_TYPES.map(({ id, label }) => {
              const cap = capacitacionesById[id];
              const isComplete = cap?.status === "completada" || Boolean(cap?.key);

              return (
                <Box
                  key={id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.6,
                    gap: 0.5,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: C.textSoft, flex: 1 }} noWrap>
                    {label}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {isComplete ? (
                      <>
                        <Chip
                          size="small"
                          icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                          label="Completada"
                          sx={{
                            height: 18,
                            fontSize: 9,
                            bgcolor: "#dcfce7",
                            color: "#16a34a",
                            fontWeight: 600,
                            "& .MuiChip-icon": { color: "#16a34a" },
                          }}
                        />
                        {cap?.key && (
                          <Tooltip title="Ver constancia">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocument(cap.key)}
                            >
                              <VisibilityIcon sx={{ fontSize: 16, color: C.primary }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <Chip
                        size="small"
                        label="Pendiente"
                        sx={{
                          height: 18,
                          fontSize: 9,
                          bgcolor: "#fef3c7",
                          color: C.warning,
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {canEdit && (
                      <CapacitacionUploadButton
                        uploading={uploadingKey === `cap-${id}`}
                        onSelect={(file) => handleUploadCapacitacion(id, file)}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </SectionCard>
        </Box>
      )}
    </Box>
  );
}

function CapacitacionUploadButton({ uploading, onSelect }) {
  const inputRef = useRef(null);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_EXTS}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onSelect(file);
        }}
      />
      <Tooltip title="Subir constancia">
        <span>
          <IconButton
            size="small"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <CircularProgress size={14} />
            ) : (
              <UploadFileIcon sx={{ fontSize: 16, color: C.primary }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
}
